import React from "react";
import { styled } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { TILE_SIZE, TILE_SIZE_RESPONSIVE } from "./constants";
import { useSelector } from "react-redux";
import ConfiguratorIcons from "./ConfiguratorIcons";
import { TileSide } from "./TileSide";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../helpers";
import {
  getTileStyle,
  handleTileClick,
  isTileAvailableForSpecialObjects,
} from "./utils/tileUtils";

const PREFIX = "Tile";

const classes = {
  root: `${PREFIX}-root`,
  tileHover: `${PREFIX}-tileHover`,
  tileStyle: `${PREFIX}-tileStyle`,
  tileStyleDefault: `${PREFIX}-tileStyleDefault`,
  multimodeColor: `${PREFIX}-multimodeColor`,
  desactivatedTile: `${PREFIX}-desactivatedTile`,
  multimodeSelected: `${PREFIX}-multimodeSelected`,
  tileStyleFirstRow: `${PREFIX}-tileStyleFirstRow`,
  tileStyleAllRows: `${PREFIX}-tileStyleAllRows`,
  tileStyleFirstColumn: `${PREFIX}-tileStyleFirstColumn`,
  tileCenterColor: `${PREFIX}-tileCenterColor`,
  finalBorderColorEvent: `${PREFIX}-finalBorderColorEvent`,
  finalBorderColorTheme: `${PREFIX}-finalBorderColorTheme`,
  activeTile: `${PREFIX}-activeTile`,
};

const TileRoot = styled("div")(({ theme }) => {
  return {
    [`& .${classes.tileCenterColor}`]: {
      backgroundColor: "rgba(128, 128, 128 , 0.3)",
    },
    [`& .${classes.tileHover}`]: {
      "&:hover": {
        backgroundColor: "rgba(211, 175, 95, 1)",
        cursor: "pointer",
      },
    },
    [`& .${classes.tileStyle}`]: {
      position: "relative",
      [theme.breakpoints.between("xs", "sm")]: {
        height: `${TILE_SIZE_RESPONSIVE}px`,
        width: `${TILE_SIZE_RESPONSIVE}px`,
      },
      height: `${TILE_SIZE}px`,
      width: `${TILE_SIZE}px`,
      zIndex: "99",
    },
    [`& .${classes.multimodeColor}`]: {
      backgroundColor: "rgba(211, 175, 95, 0.35)",
    },
    [`& .${classes.multimodeSelected}`]: {
      backgroundColor: "rgba(211, 175, 95, 1)",
    },
    [`& .${classes.finalBorderColorEvent}`]: {
      borderColor: "rgba(211, 175, 95, 1)",
    },
    [`& .${classes.finalBorderColorTheme}`]: {
      borderColor: "rgba(60, 179, 113, 1)",
    },
    [`& .${classes.activeTile}`]: {
      backgroundColor: "rgba(211, 175, 95, 0.6)",
    },
    [`& .${classes.desactivatedTile}`]: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
  };
});

export const Tile = ({ tile }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { width } = useViewport();
  const multimode = useSelector((state) => state.configurator?.multiSelection);
  const multiUpload = useSelector((state) => state.configurator.multiUpload);
  const currentLayout = useSelector((state) => state.configurator?.layout);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const parentedLayoutsList = useSelector(
    (state) => state.configurator.parentedLayoutsList
  );
  const selectedTile = useSelector((state) => state.configurator.selectedTile);
  const lastMainVideo = useSelector(
    (state) => state.configurator.lastMainVideo
  );
  const rowNumber = useSelector((state) => state.configurator.rowNumber);
  const columnNumber = useSelector((state) => state.configurator.columnNumber);
  const wallOptions = useSelector(
    (state) => state.configurator?.wallsManagement
  );
  const myGrid = useSelector((state) => state.configurator?.grid);

  const isAvailable = multimode.active
    ? isTileAvailableForSpecialObjects(tile, tile.row, tile.column, multimode)
    : null;

  const getTileBorder = (direction) => {
    // let solid = `1px solid ${currentLayout === 'event' ? `rgba(211, 175, 95, 1)` : currentLayout === "theme" ? 'rgba(60, 179, 113, 1)' : 'rgba(255, 255, 255, 1)'}`;
    let solid = tile.walls[direction].color
      ? `1px solid rgba(${tile.walls[direction].color.r}, ${tile.walls[direction].color.g}, ${tile.walls[direction].color.b}, ${tile.walls[direction].color.a})`
      : `1px solid rgba(${wallOptions.color.r}, ${wallOptions.color.g}, ${wallOptions.color.b}, ${wallOptions.color.a})`;
    let dashed = `1px dashed ${
      currentLayout === "event"
        ? `rgba(211, 175, 95, 1)`
        : currentLayout === "theme"
        ? "rgba(60, 179, 113, 1)"
        : currentLayout === "walls"
        ? "rgba(196, 77, 86, 1)"
        : "rgba(255, 255, 255, 1)"
    }`;

    if (direction === "north" || direction === "east") {
      if (tile.walls[direction] !== "empty") {
        return solid;
      } else if (tile.walls[direction] === "none") {
        return "none";
      } else {
        return dashed;
      }
    } else {
      if (tile.column === 0 && direction !== "south") {
        if (tile.walls[direction] !== "empty") {
          return solid;
        } else if (tile.walls[direction] === "none") {
          return "none";
        } else {
          return dashed;
        }
      } else if (tile.row + 1 === rowNumber && direction === "south") {
        if (tile.walls[direction] !== "empty") {
          return solid;
        } else if (tile.walls[direction] === "none") {
          return "none";
        } else {
          return dashed;
        }
      } else {
        if (tile.walls[direction] !== "empty") {
          return solid;
        }
      }
    }
  };

  return (
    <TileRoot>
      <div
        className={`${classes.tileStyle} ${getTileStyle(
          tile,
          tile.row,
          tile.column,
          rowNumber,
          columnNumber,
          multimode,
          classes,
          selectedTile,
          currentLayout,
          isAvailable,
          width
        )}`}
        onClick={
          isAvailable === false
            ? undefined
            : () =>
                handleTileClick(
                  tile,
                  parentLayout,
                  currentLayout,
                  parentedLayoutsList,
                  wallOptions,
                  multimode,
                  lastMainVideo,
                  multiUpload,
                  myGrid,
                  dispatch,
                  t
                )
        }
        style={{
          borderTop: getTileBorder("north"),
          borderRight: getTileBorder("east"),
          borderBottom: getTileBorder("south"),
          borderLeft: getTileBorder("west"),
        }}
      >
        <ConfiguratorIcons
          orientation={tile.middle}
          tile={{ row: tile.row, column: tile.column }}
          side={
            tile.middle.peer &&
            tile.row === tile.middle.startTile.row &&
            tile.column === tile.middle.startTile.column
              ? tile.middle.peer.start
              : tile.middle.peer &&
                tile.row === tile.middle.endTile.row &&
                tile.column === tile.middle.endTile.column
              ? tile.middle.peer.end
              : null
          }
        />
        <ConfiguratorIcons orientation={tile.theme} />
        <ConfiguratorIcons orientation={tile.event} />
        <ConfiguratorIcons orientation={tile.north} />
        <ConfiguratorIcons orientation={tile.east} />
        <ConfiguratorIcons orientation={tile.west} />
        <ConfiguratorIcons orientation={tile.south} />
        <TileSide tile={tile} />
      </div>
    </TileRoot>
  );
};
